import React, { ReactElement, useEffect, useCallback } from 'react';

import { useSubmitClaimWorkflow } from '../../claims-center/submit-claims/useSubmitClaimWorkflow';
import { useSelectedShopId } from '../../../hooks/useSelectedShop';
import { useClaimsShopCarriers } from './common/useClaimsShopCarriers';
import { useIsOrderItemsRequired } from './common/useIsOrderItemsRequired';
import useInvoiceExtraction from './common/useInvoiceExtraction';
import { useImportDocuments } from './common/useImportDocuments';
import { useDocumentsViewer } from './documents-viewer/useDocumentsViewer';

import { ClaimType, ClaimContext, OnChangeClaimContext, SubmitClaimEntryPoint, StepProps, ClaimShopCarrier} from './types';
import { ClaimStepContainerProps } from './common/ClaimStepContainer';

import InvestigationStepContainer from './investigation/InvestigationStepContainer';
import PartialDamageStepContainer from './partial-damage/PartialDamageStepContainer';
import MissingItemStepContainer from './missing-item/MissingItemStepContainer';
import WholeDamageStepContainer from './whole-damage/WholeDamageStepContainer';
import CashOnDeliveryStepContainer from './cash-on-delivery/CashOnDeliveryStepContainer';

import Switch from '../../Wizard/Switch';
import ChooseClaimTypeStep from './ChooseClaimTypeStep';
import ChooseDamageTypeStep from './ChooseDamageTypeStep';
import OutsideOfDeadlineStep from './common/OutsideOfDeadlineStep';
import DeadlineShippingInfoStep from './common/DeadlineShippingInfoStep';
import WarehouseDeliveryConfirmationStep from './common/WarehouseDeliveryConfirmationStep';
import ManualDeadlineCheckStep from './common/ManualDeadlineCheckStep';
import InvestigationDeliveredToMailboxStep from './investigation/InvestigationDeliveredToMailboxStep';
import EasyShippingInfoStep from './common/EasyShippingInfoStep';
import SearchClaimInfoStep from './common/SearchClaimInfoStep';
import DocumentsViewer from './documents-viewer/DocumentsViewer';
import InvoiceStep from './common/InvoiceStep';
import AdditionalInfoStep from './common/AdditionalInfoStep';
import { StateValue } from 'xstate';
import DraftLoadingStep from './common/DraftLoadingStep';
import DORSigningLinkStep from './investigation/DORSigningLinkStep';
import { useQuery } from '@tanstack/react-query';
import container from '../../../container';
import { useClaimsShopSettings } from '../ClaimsShopSettingsContext';
import { isSpecialInvestigationFlow, isSpecialInvestigationFlowClaimComplete } from './claimsHelpers';
import PictureUploadLinkStep from './common/PictureUploadLinkStep';
import { useFlag } from '@unleash/proxy-client-react';
import { SearchShipmentResult } from '../../claims-center/submit-claims/useSearchShipment';

interface StepContainerMap {
  [key: string]: React.ComponentType<ClaimStepContainerProps>;
}

const stepContainersByClaimType: StepContainerMap = {
  [ClaimType.INVESTIGATION]: InvestigationStepContainer,
  [ClaimType.PARTIAL_DAMAGE]: PartialDamageStepContainer,
  [ClaimType.WHOLE_DAMAGE]: WholeDamageStepContainer,
  [ClaimType.MISSING_ITEM]: MissingItemStepContainer,
  [ClaimType.CASH_ON_DELIVERY]: CashOnDeliveryStepContainer,
};

interface CreateClaimProps {
  claim: ClaimContext;
  nonClaimableMessage?: string;
  onChange?: OnChangeClaimContext;
  onSubmit?: () => void;
  onSaveDraft?: () => void;
  resetClaim?: () => void;
  onLoadShipmentByTrackingId?: (trackingId: string) => Promise<SearchShipmentResult>;
  onResetShipmentSearch?: () => void;
  onClose?: (step: StateValue) => void;
  isSubmitting?: boolean; // indicates 2 things - search is in progress or claim is being submitted
  isClaimDraftSaving?: boolean;
  isDisabled?: boolean;
}

const emptyCarriers: ClaimShopCarrier[] = [];

// used by parcel finder and claims center
function CreateClaim(props: CreateClaimProps): ReactElement {
  const {
    claim,
    nonClaimableMessage,
    onChange = () => {},
    onSubmit,
    onSaveDraft,
    resetClaim = () => {},
    onLoadShipmentByTrackingId = () => Promise.resolve({ isClaimSubmissionBlocked: true }),
    onResetShipmentSearch = () => {},
    isSubmitting, // indicates 2 things - search is in progress or claim is being submitted
    isClaimDraftSaving,
    isDisabled,
  } = props;

  const { carrierCode, carrierCountryCode, isOrderItemTaxRate } = claim;

  const shopId = useSelectedShopId();
  const shopSettings = useClaimsShopSettings();
  const shouldForceShowEDORStep = useFlag('one-touch-claiming-awareness');

  const { workflowStep, transition, next, back, canonicalWorkflowStep } = useSubmitClaimWorkflow({
    ...claim,
    shopSettings,
    shouldForceShowEDORStep,
  });

  const isSpecialFlow = isSpecialInvestigationFlow({ shopSettings, type: claim.type });

  // In the special flow the submit claim button only appears once certain fields are filled,
  // otherwise it will show save draft
  const isSubmitEnabledOnLastStep =
    !isSpecialFlow || isSpecialInvestigationFlowClaimComplete({ ...claim, shopSettings });

  const onResetClaim = (): void => {
    resetClaim();
  };

  const onSelectDamage = (): void => transition('SELECT_DAMAGE');

  const createOnSelectClaimType = (event: string, type: ClaimType) => (): void => {
    transition(event, { context: { type } });
    onChange({ type });

    // In Parcel Finder we trigger import after claim type is selected
    if (claim.entryPoint === SubmitClaimEntryPoint.PARCEL_FINDER) {
      importDocuments({ ...claim, type });
    }
  };

  const onSelectInvestigation = createOnSelectClaimType('SELECT_INVESTIGATION', ClaimType.INVESTIGATION);
  const onSelectPartialDamage = createOnSelectClaimType('SELECT_PARTIAL_DAMAGE', ClaimType.PARTIAL_DAMAGE);
  const onSelectWholeDamage = createOnSelectClaimType('SELECT_WHOLE_DAMAGE', ClaimType.WHOLE_DAMAGE);
  const onSelectMissingItem = createOnSelectClaimType('SELECT_MISSING_ITEM', ClaimType.MISSING_ITEM);
  const onSelectCashOnDelivery = createOnSelectClaimType('SELECT_CASH_ON_DELIVERY', ClaimType.CASH_ON_DELIVERY);
  const onClose = (): void => props.onClose?.(canonicalWorkflowStep);

  const { data: carriers = emptyCarriers } = useClaimsShopCarriers();

  const carrierSlug = `${carrierCode}-${carrierCountryCode}`;
  const availableDorLanguagesQueryResult = useQuery<string[]>({
    queryKey: ['availableDorLanguages', shopId, carrierSlug, !!claim.isReturnShipment, claim.recipientCountryCode],
    queryFn: () =>
      container.touchpointsApi.getAvailableDorLanguages(
        shopId,
        carrierSlug,
        !!claim.isReturnShipment,
        claim.recipientCountryCode || undefined
      ),
    enabled: Boolean(shopId && carrierCode && carrierCountryCode),
  });

  // finds selected carrier and adds it to the claim context
  useEffect(() => {
    const selectedCarrier = carriers.find(
      ({ code, countryCode }) => code === claim.carrierCode && countryCode === claim.carrierCountryCode
    );
    onChange({
      carrier: selectedCarrier,
    });
  }, [carriers, claim.carrierCode, claim.carrierCountryCode, onChange]);

  const { isLoading: isLoadingIsOrderItemsRequired } = useIsOrderItemsRequired(claim, onChange);

  const { isExtractingInvoice, isFailedToExtractInvoice, extractInvoiceData } = useInvoiceExtraction(
    claim,
    onChange,
    !!isOrderItemTaxRate,
    claim.type === ClaimType.CASH_ON_DELIVERY,
    shopId
  );

  const { importDocuments, importDORState, importInvoiceState, importConsentState } = useImportDocuments({
    shopId,
    onChange,
    extractInvoiceData,
  });

  const { documentsViewerButtonElement, documentViewerFiles, isDocumentsViewerOpen } = useDocumentsViewer({
    claim,
    workflowStep,
  });

  const onSearchNextClick = useCallback(() => {
    // In Claims Center we trigger import after shipment is searched and claim context initialized (luckily)
    importDocuments(claim);
    next();
  }, [importDocuments, next, claim]);

  const stepProps = {
    shopId,
    claim: { ...claim, shouldForceShowEDORStep },
    carriers,
    nonClaimableMessage,
    as: (claim.type && stepContainersByClaimType[claim.type]) || React.Fragment,
    onChange,
    onNext: next,
    onBack: back,
    onClose,
    onSaveDraft: isSpecialFlow ? null : onSaveDraft,
    headerLeftElement: documentsViewerButtonElement,
    onResetClaim,
    onResetShipmentSearch,
    onLoadShipmentByTrackingId,
    isClaimDraftSaving,
    isSubmitting,
    isDisabled,
    isManual: !claim.shipmentId,
    isLoadingIsOrderItemsRequired,
    isExtractingInvoice,
    isFailedToExtractInvoice,
    extractInvoiceData,
  } satisfies StepProps;

  return (
    <React.Fragment>
      {isDocumentsViewerOpen && <DocumentsViewer files={documentViewerFiles} />}
      <Switch selected={workflowStep}>
        <DraftLoadingStep key="draft-loading" name="draft-loading" onDraftLoaded={importDocuments} {...stepProps} />
        <ChooseClaimTypeStep
          key="choose-claim-type"
          name="choose-claim-type"
          onClickDamage={onSelectDamage}
          onClickMissingItem={onSelectMissingItem}
          onClickInvestigation={onSelectInvestigation}
          onClickCashOnDelivery={onSelectCashOnDelivery}
          claim={claim}
          onClose={onClose}
        />
        <ChooseDamageTypeStep
          key="choose-damage-type"
          name="choose-damage-type"
          onClickPartialDamage={onSelectPartialDamage}
          onClickWholeDamage={onSelectWholeDamage}
          onBack={back}
          onClose={onClose}
        />
        <SearchClaimInfoStep
          key="search-claim-info"
          name="search-claim-info"
          {...stepProps}
          onNext={onSearchNextClick}
          onSaveDraft={null}
        />
        <ManualDeadlineCheckStep key="manual-deadline-check" name="manual-deadline-check" {...stepProps} />
        <EasyShippingInfoStep key="easy-shipping-info" name="easy-shipping-info" {...stepProps} />
        <OutsideOfDeadlineStep key="outside-of-deadline" name="outside-of-deadline" {...stepProps} />
        <DeadlineShippingInfoStep key="deadline-shipping-info" name="deadline-shipping-info" {...stepProps} />
        <InvestigationDeliveredToMailboxStep
          key="investigation-delivered-to-mailbox"
          name="investigation-delivered-to-mailbox"
          {...stepProps}
        />
        <WarehouseDeliveryConfirmationStep
          key="warehouse-delivery-confirmation-info"
          name="warehouse-delivery-confirmation-info"
          {...stepProps}
        />
        <InvoiceStep key="invoice-step" name="invoice-step" importInvoiceState={importInvoiceState} {...stepProps} />
        <InvoiceStep
          key="invoice-step-final"
          name="invoice-step"
          importInvoiceState={importInvoiceState}
          {...stepProps}
          onNext={isSubmitEnabledOnLastStep ? onSubmit : null}
          isSubmitButton={isSubmitEnabledOnLastStep}
          onSaveDraft={isSpecialFlow && isSubmitEnabledOnLastStep ? null : onSaveDraft}
        />
        <AdditionalInfoStep
          key="additional-info-step"
          name="additional-info-step"
          importDORState={importDORState}
          importConsentState={importConsentState}
          {...stepProps}
          onNext={isSubmitEnabledOnLastStep ? onSubmit : null}
          isSubmitButton={isSubmitEnabledOnLastStep}
          onSaveDraft={isSpecialFlow && isSubmitEnabledOnLastStep ? null : onSaveDraft}
        />
        <PictureUploadLinkStep key="picture-upload-link-step" name="picture-upload-link-step" {...stepProps} />
        <DORSigningLinkStep
          key="create-dor-step"
          name="create-dor-step"
          {...stepProps}
          availableDorLanguagesQueryResult={availableDorLanguagesQueryResult}
        />
      </Switch>
    </React.Fragment>
  );
}

export default React.memo(CreateClaim);
